import { svgIconPaths } from '@/assets/svg/icons';
import CustomButton from '@/components/atoms/CustomButton';
import SidebarItemViewTemplate from '@/components/templates/SidebarItemViewTemplate';
import queryKeys from '@/constants/queryKeys';
import useCreateBrandedContent from '@/hooks/content/useCreateBrandedContent';
import useContentImageUpload from '@/hooks/content/useContentImageUpload';
import useToast from '@/hooks/useToast';
import BrandedContentWizard, { TBrandedFormMode } from '@/modules/content/branded/views/BrandedContentWizard';
import { buildContentDetails, collectImageUploads } from '@/modules/content/branded/contentPayload';
import { TBrandedFormSubmit } from '@/modules/content/branded/types';
import { getContentTypeLabel } from '@/modules/content/common/contentDisplay';
import {
  createMuxUploadUrl,
  publishContent,
  setYoutubeVideoSource,
} from '@/services/api/requests/content';
import { uploadFileToMux } from '@/services/muxUpload';
import { TCustomAxiosError } from '@/services/api';
import colors from '@/theme/colors';
import { TContentType } from '@/types/Content.types';
import { HStack, Text, VStack } from '@chakra-ui/react';
import { useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import React, { useState } from 'react';

const SUPPORTED_TYPES: TContentType[] = ['post', 'article', 'video'];

const CreateBrandedContent = () => {
  const { type } = Route.useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();

  const contentType = type as TContentType;
  const isSupported = SUPPORTED_TYPES.includes(contentType);

  const { mutateAsync: createContent } = useCreateBrandedContent();
  const { uploadThumbnailAsync, uploadArticleImageAsync } = useContentImageUpload();
  const [isSaving, setIsSaving] = useState(false);
  const [uploadPercent, setUploadPercent] = useState<number | null>(null);

  const goToList = () => navigate({ to: '/content/branded-content' });
  const goToEdit = (id: string) =>
    navigate({ to: '/content/branded-content/$contentId', params: { contentId: id } });

  const handleSubmit = async (mode: TBrandedFormMode, { values, thumbnailFile }: TBrandedFormSubmit) => {
    setIsSaving(true);
    try {
      const created = await createContent({
        type: contentType,
        title: values.title,
        description: values.description,
        domains: values.domains,
        subdomains: values.subdomains,
        details: buildContentDetails(contentType, values),
      });

      const id = created.data.id;

      if (thumbnailFile) {
        await uploadThumbnailAsync({ id, file: thumbnailFile });
      }

      if (contentType === 'video') {
        // Set the chosen source. YouTube can publish immediately; a Mux upload must
        // finish processing (webhook → status 'ready') before it can be published,
        // so we always land on the edit page where status + publish are surfaced.
        let publishedYoutube = false;
        if (values.videoSource === 'youtube' && values.youtubeUrl.trim()) {
          await setYoutubeVideoSource(id, values.youtubeUrl.trim());
          if (mode === 'publish') {
            await publishContent(id);
            publishedYoutube = true;
          }
        } else if (values.videoSource === 'mux' && values.videoFile) {
          const { uploadUrl } = await createMuxUploadUrl(id);
          setUploadPercent(0);
          await uploadFileToMux(uploadUrl, values.videoFile, setUploadPercent);
          setUploadPercent(null);
        }

        await queryClient.invalidateQueries({ queryKey: [queryKeys.content.all] });
        toast({
          status: 'success',
          title: 'Success',
          description: publishedYoutube
            ? 'Reel published.'
            : values.videoSource === 'mux'
              ? 'Video uploaded. It can be published once Mux finishes processing.'
              : 'Draft saved.',
        });
        goToEdit(id);
        return;
      }

      for (const { sectionId, subsectionId, file } of collectImageUploads(values)) {
        await uploadArticleImageAsync({ id, file, sectionId, subsectionId });
      }

      if (mode === 'publish') {
        await publishContent(id);
      }

      await queryClient.invalidateQueries({ queryKey: [queryKeys.content.all] });
      toast({
        status: 'success',
        title: 'Success',
        description: mode === 'publish' ? 'MyOmnia content published.' : 'Draft saved.',
      });
      goToList();
    } catch (error) {
      setUploadPercent(null);
      toast({
        status: 'error',
        title: 'Error saving content',
        description: (error as TCustomAxiosError)?.errors?.[0]?.message,
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <SidebarItemViewTemplate title={`New MyOmnia ${isSupported ? getContentTypeLabel(contentType) : 'Content'}`}>
      <HStack justify="flex-start" width="100%" marginTop="20px">
        <CustomButton
          isTransparent
          iconBeforeLabelSvgPath={svgIconPaths.leftArrow}
          labelColor={colors.background.blueGray}
          labelHoverColor="extra.black"
          label="Back to Branded Content"
          hoverBackgroundColor="transparent"
          borderHoverColor="transparent"
          onClick={goToList}
          labelStyle={{ marginBottom: '5px', fontSize: '16px' }}
        />
      </HStack>

      {uploadPercent !== null && (
        <Text color="text.mediumBlue" fontWeight={600} marginTop="10px">
          Uploading video… {uploadPercent}%
        </Text>
      )}

      {!isSupported ? (
        <VStack align="flex-start" width="100%" marginTop="20px">
          <Text color="text.mediumBlue">
            This content type isn’t available from the backoffice yet.
          </Text>
        </VStack>
      ) : (
        <BrandedContentWizard
          type={contentType}
          isSubmitting={isSaving}
          onExit={goToList}
          onSubmit={handleSubmit}
          secondaryAction={{ key: 'draft', label: 'Save draft', mode: 'draft', isTransparent: true }}
          primaryAction={{
            key: 'publish',
            label: 'Publish Content',
            mode: 'publish',
            backgroundColor: 'extra.orange',
          }}
        />
      )}
    </SidebarItemViewTemplate>
  );
};

export const Route = createFileRoute('/_authenticated/_admin/content/branded-content/create/$type')({
  component: CreateBrandedContent,
});
